import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Πλάτος ενός στοιχείου (clientWidth, όπως το φύλλο — η εφαρμογή κάνει zoom, όχι το παράθυρο), με
 * ResizeObserver. Τα γραφήματα ζωγραφίζονται σε pixel αυτού του πλάτους, ώστε τα κείμενα να μη
 * «τεντώνονται» όπως με viewBox. Callback ref: δουλεύει και όταν το στοιχείο εμφανίζεται αργότερα.
 * 0 (jsdom, πριν από τη μέτρηση) → `fallback`.
 */
export function useWidth<T extends HTMLElement = HTMLDivElement>(fallback = 640): [(node: T | null) => void, number] {
  const [el, setEl] = useState<T | null>(null);
  const [width, setWidth] = useState(0);
  const ref = useCallback((node: T | null) => setEl(node), []);
  useLayoutEffect(() => {
    if (!el) return undefined;
    const measure = () => setWidth(prev => (prev === el.clientWidth ? prev : el.clientWidth));
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [el]);
  return [ref, width > 0 ? width : fallback];
}
